import * as Y from '@y/y'
import { Plugin } from 'prosemirror-state'
import {
  defaultAttributedNodes,
  defaultMapAttributionToMark
} from './sync-utils.js'
import { YSyncRdt } from './rdt/y-sync.js'
import { ProsemirrorRdt } from './rdt/prosemirror.js'
import { defaultSwallowedFormats } from './transformers/swallow-formats.js'
import { defaultTransformer } from './convert.js'
import { biasCaretLeft, isBackwardDeletion } from './suggestion-caret.js'
import { bind, Binding } from 'lib0/delta/rdt'
import { ySyncPluginKey } from './keys.js'
import * as s from 'lib0/schema'
import * as object from 'lib0/object'

/**
 * The y-prosemirror binding is a bi-directional synchronization with the provided Y.Node and the EditorView
 * Any change applied to the EditorView will be applied (via deltas) to the Y.Node, and vice versa.
 */
export const $syncPluginState = s.$object({
  ytype: Y.$nodeAny.nullable,
  /**
   * If provided, will switch to the given renderer instead of the current renderer
   */
  renderer: Y.$renderer.nullable,
  attributionMapper: /** @type {s.Schema<AttributionMapper>} */ (s.$function),
  /**
   * Predicate deciding which attributed nodes render under their
   * `{nodeName}--attributed` variant. See {@link syncPlugin}.
   */
  attributedNodes: /** @type {s.Schema<AttributedNodesPredicate>} */ (s.$function),
  /**
   * Custom pairing predicate that shifts the diffing boundary (forwarded to
   * `lib0/delta.diff` as its `compare` option). `null` keeps lib0's name-only
   * default. See {@link NodeCompare} and {@link syncPlugin}.
   */
  customCompare: /** @type {s.Schema<NodeCompare>} */ (s.$function).nullable,
  /**
   * Predicate deciding whether the current ProseMirror document is the
   * integrator's initial (empty) state. `null` keeps the schema-default
   * fingerprint check. See {@link IsInitialContent} and {@link syncPlugin}.
   */
  isInitialContent: /** @type {s.Schema<IsInitialContent>} */ (s.$function).nullable,
  /**
   * The live RDT binding (null while paused / before the first setup). `binding.t` is the
   * data(Y render)⇄view(PM doc) transformer that cursor positions are mapped through.
   */
  binding: /** @type {s.Schema<import('lib0/delta/rdt').Binding<any, any>>} */ (s.$instanceOf(Binding)).nullable
})

export const $syncPluginStateUpdate = s.$object({
  ytype: Y.$nodeAny.nullable.optional,
  renderer: Y.$renderer.nullable.optional,
  attributionMapper: /** @type {s.Schema<AttributionMapper>} */ (s.$function).nullable.optional,
  attributedNodes: /** @type {s.Schema<AttributedNodesPredicate>} */ (s.$function).nullable.optional,
  customCompare: /** @type {s.Schema<NodeCompare>} */ (s.$function).nullable.optional,
  isInitialContent: /** @type {s.Schema<IsInitialContent>} */ (s.$function).nullable.optional,
  binding: /** @type {s.Schema<import('lib0/delta/rdt').Binding<any, any>>} */ (s.$instanceOf(Binding)).nullable.optional,
  change: /** @type {s.Schema<Y.YEvent<any>>} */ (s.$any).nullable.optional
})
const $maybeSyncPluginStateUpdate = $syncPluginStateUpdate.nullable

/**
 * The binding transformer for a sync plugin state, but only when the binding actually
 * belongs to the state's current ytype/renderer. During a `configureYProsemirror`
 * dispatch a plugin-state overlay can pair a NEW ytype with the not-yet-replaced OLD
 * binding (`setup()` runs later, in the sync plugin's view update) - mapping positions
 * through it would resolve them against the wrong render. A destroyed binding (carried
 * by an EditorState whose view was destroyed) is never usable either.
 *
 * @param {{ytype: Y.Node | null, renderer: Y.AbstractRenderer | null, binding?: import('lib0/delta/rdt').Binding<any, any> | null} | undefined} ystate
 * @return {import('lib0/delta/transformer').Transformer<any, any> | null}
 */
export const usableTransformer = (ystate) => {
  const binding = ystate?.binding
  if (binding == null) {
    return null
  }
  const yRdt = /** @type {import('./rdt/y-sync.js').YSyncRdt} */ (binding.a)
  // a retained EditorState outlives its view: the binding it carries is torn
  // down with that view and its transformer no longer tracks the ytype
  return (!yRdt.destroyed && yRdt.ytype === ystate?.ytype && (yRdt.renderer ?? null) === (ystate?.renderer ?? null))
    ? binding.t
    : null
}

/**
 * @typedef {{ yRdt: YSyncRdt, pmRdt: ProsemirrorRdt, binding: import('lib0/delta/rdt').Binding<any, any> }} SyncRdts
 */

/**
 * @param {SyncRdts} rdts
 */
const destroyRdts = rdts => {
  rdts.binding.destroy()
  rdts.yRdt.destroy()
  rdts.pmRdt.destroy()
}

/**
 * Whether the binding must be rebuilt to go from plugin state `a` to `b`.
 *
 * @param {SyncPluginState} a
 * @param {SyncPluginState} b
 * @return {boolean}
 */
const syncConfigChanged = (a, b) =>
  a.ytype !== b.ytype ||
  a.renderer !== b.renderer ||
  a.attributionMapper !== b.attributionMapper ||
  a.attributedNodes !== b.attributedNodes ||
  a.customCompare !== b.customCompare ||
  a.isInitialContent !== b.isInitialContent

/**
 * Schemas already audited by {@link warnUnsupportedAttributionMarks}, so a
 * ytype/renderer switch (which rebuilds the binding) does not re-log.
 *
 * @type {WeakSet<import('prosemirror-model').Schema>}
 */
const auditedSchemas = new WeakSet()

/**
 * Warn once per schema about node types that cannot hold the reserved
 * `y-attributed-*` marks the binding renders.
 *
 * **Only call this for a binding that has a renderer.** Attribution is
 * produced exclusively by the renderer, so a renderer-less binding applies no
 * attribution marks and has nothing to audit. The caller enforces that; the
 * helper assumes it, and treats a schema declaring none of the marks as a
 * misconfiguration rather than an opt-out.
 *
 * Attribution is a *projection* of the Y side and is written to the view as
 * marks. When the target node's schema does not admit them, ProseMirror drops
 * them silently (`tr.addMark` checks `parent.type.allowsMarkType`) or throws
 * from `tr.addNodeMark`, and the reverse leg swallows the loss (see
 * {@link import('./transformers/swallow-formats.js').swallowFormats}) - so the view renders stale attribution with no
 * error to go on. The check is cheap, deterministic and runs before any
 * editing, which makes it a far better diagnostic than the transformer's
 * per-change warning: at swallow time a schema refusal is indistinguishable
 * from an ordinary edit to the projection.
 *
 * Leaf node types are skipped - they hold no content that could carry a mark.
 * The test is `NodeType.allowsMarkType`, i.e. the *resolved* `markSet`, not
 * the `marks:` spec string: ProseMirror resolves an omitted `marks:` on a
 * node without inline content to `[]`, so a container can exclude the marks
 * without ever writing `marks: ''`.
 *
 * @param {import('prosemirror-model').Schema} schema
 */
const warnUnsupportedAttributionMarks = (schema) => {
  if (auditedSchemas.has(schema)) return
  auditedSchemas.add(schema)
  const markTypes = defaultSwallowedFormats
    .map(name => schema.marks[name])
    .filter(markType => markType != null)
  if (markTypes.length === 0) {
    // the caller only gets here with a renderer configured, so this is a
    // misconfiguration rather than an opt-out: suggestions were asked for and
    // the schema has nothing to display them with
    console.warn(
      '[y/prosemirror] a renderer is configured (suggestions / versioning), but this schema ' +
      'declares none of the y-attributed-* marks - no attribution will be rendered. Declare ' +
      'y-attributed-insert / -delete / -format (and y-attributed-attrs for node-attribute ' +
      'changes) and whitelist them on every node that holds attributable content. See ' +
      'ATTRIBUTION.md.'
    )
    return
  }
  /**
   * @type {Array<string>}
   */
  const offenders = []
  for (const nodeName in schema.nodes) {
    const nodeType = schema.nodes[nodeName]
    if (nodeType.isLeaf) continue // no content to carry a mark
    const missing = markTypes.filter(markType => !nodeType.allowsMarkType(markType))
    if (missing.length > 0) {
      offenders.push(`  ${nodeName}: ${missing.map(m => m.name).join(', ')}`)
    }
  }
  if (offenders.length === 0) return
  console.warn(
    '[y/prosemirror] these node types do not allow the attribution marks this binding renders:\n' +
    offenders.join('\n') +
    '\nProseMirror drops those marks silently (or throws from `tr.addNodeMark`) and the binding ' +
    'swallows the loss, so attribution will not render inside those nodes. Add the marks by name ' +
    "to each node's `marks` content expression, or extend its `markSet` after editor construction."
  )
}

/**
 * This Prosemirror {@link Plugin} is responsible for synchronizing the prosemirror {@link EditorState} with a {@link Y.XmlFragment}
 *
 * The two sides are modeled as lib0 `RDT`s ({@link YSyncRdt} around the ytype,
 * {@link ProsemirrorRdt} around the view) connected through the
 * {@link defaultTransformer} pipeline (`lib0/delta/rdt.bind`):
 *
 *     YSyncRdt ⇄ pipe(renderedAttributions, inlineAnonymousNodes, ...opts.transformers, attributionToFormat, swallowFormats) ⇄ ProsemirrorRdt
 *
 * {@link import('./convert.js').ynodeToPmnode} and
 * {@link import('./convert.js').pmnodeToDelta} map through the same
 * pipeline without a binding.
 *
 * Data → view (`applyA`), the pipeline expands each change's attribution to
 * the full accumulated attribution (`renderedAttributions`) and renders it into
 * the reserved `y-attributed-*` format keys (`attributionToFormat`) that the
 * view applies as marks. View → data (`applyB`), `swallowFormats` gates those
 * keys: a view-side *removal* is dropped (nothing reaches Y, nothing is pushed
 * back) and a view-side *addition* is dropped and corrected away in the view —
 * the view never attributes; the Y side re-attributes through its renderer and
 * returns the resulting marks as a fix.
 *
 * The PM->Y pull runs in the plugin's `view().update` hook (i.e. after the
 * dispatch has been committed to the view), not in `appendTransaction`.
 * Running it in `appendTransaction` would cause speculative `state.apply`
 * callers to write to Y as a side effect.
 *
 * One plugin instance serves one live editor: mounting it (or an EditorState
 * holding it) in several live views bound to the same ytype is unsupported and
 * warns - the views would share one Y transaction origin and miss each other's
 * edits. Remounting a retained EditorState after its view was destroyed is
 * supported (the new view rebuilds the binding and catches up with Y), and a
 * plugin-list change (`state.reconfigure`) keeps the live binding.
 *
 * @param {object} opts
 * @param {Y.Doc} [opts.suggestionDoc] A {@link Y.Doc} to use for suggestion tracking
 * @param {AttributionMapper} [opts.mapAttributionToMark] A function to map the {@link Y.ContentAttribute} to a {@link import('prosemirror-model').Mark} - the mark names *must* be one of: `y-attributed-insert`, `y-attributed-delete`, `y-attributed-format`, `y-attributed-attrs`. No other mark names are permitted. `y-attributed-attrs` is the node-level mark for *attribute* changes (e.g. a suggested heading-level change): it is materialized automatically when the schema declares it (declare `attrs: { changes: { default: null } }` and — unlike the other three — keep the DEFAULT `excludes`, so a re-render *replaces* the mark instead of stacking instances). Its payload is not routed through the mapper by default; a mapper may take control by emitting the `y-attributed-attrs` key. The format attribution a span's own insert or delete attribution implies is dropped before the mapper runs (see `dropImpliedFormatAttribution` and ATTRIBUTION.md), so the inserter's own formatting renders as the insert mark alone.
 * @param {AttributedNodesPredicate} [opts.attributedNodes] Optional predicate `(nodeName, kinds) => boolean`. When it returns `true` for an attributed node *and* a `{nodeName}--attributed` type exists in the schema, that node is rendered under the variant type (the `y-attributed-*` marks are still applied). `kinds` is `{ insert?, delete?, format? }`. The variant is a pure rendering concern - the canonical name is what is stored in the Y document. The predicate must be deterministic in `(nodeName, kinds)`.
 * @param {NodeCompare} [opts.customCompare] Optional predicate `(a, b) => boolean` that shifts the *diffing boundary*. To sync, y-prosemirror diffs the ProseMirror doc against the Y document as `lib0/delta` trees; lib0's `diff` decides for each candidate node pair whether to pair them (diff *in place* via a `modify` op) or to **replace the old subtree wholesale** (delete + insert). By default a pair is matched purely on node name (`a.name === b.name`). Supply this to move the boundary - e.g. make a `blockContainer` only pair when its first child type also matches (`(a, b) => a.name === b.name && (a.name !== 'blockContainer' || firstChildName(a) === firstChildName(b))`), so changing the first child replaces the whole container instead of editing it in place. Receives the raw `lib0/delta` nodes `(fromNode, toNode)` (each exposing `.name`, `.attrs`, `.children`) and is forwarded to `lib0/delta.diff` as its `compare` option, applied recursively down the tree. Generally keep the `a.name === b.name` check; omit the option to keep lib0's name-only default.
 * @param {IsInitialContent} [opts.isInitialContent] Optional predicate `(doc) => boolean` deciding whether the current ProseMirror document is the integrator's initial (empty) state that must not be written into an empty ytype at bind time (see "Initial-content gate" in {@link ProsemirrorRdt}'s doc). Return `true` to arm the gate for this document, `false` to sync it immediately. Omit the option to keep the default check (document fingerprint equals the schema's `createAndFill()` default). Only consulted when the ytype has no children.
 * @param {Array<(($d: s.Schema<any>) => import('lib0/delta/transformer').Template<any, any>)>} [opts.transformers] Optional custom transformer stages, slotted into the pipeline **between** the built-in compat flattening stage ({@link import('./transformers/inline-anonymous-nodes.js').inlineAnonymousNodes}) and `attributionToFormat`, in data→view (`applyA`) order (i.e. before the closing `attributionToFormat` / `swallowFormats` pair). Each is a `$d => Template` factory (see `lib0/delta/transformer`); the input schema is threaded left to right. Custom transformers see changes in the flattened document space (old-representation anonymous text containers already spliced into their parents), with the complete accumulated attribution on every attribution-bearing op.
 * @param {null|((err:Error,errCode:number)=>any)} [opts.onInternalError] Listen to internal
 * errors for debugging purposes. This API is unstable and can be changed/removed at any time!
 * (errCode 0: the Y-side write failed, 1: the Y-side fix diff failed, 2: the view-side reconcile diff failed)
 * @returns {Plugin}
 */
export const syncPlugin = (opts = {}) => {
  /**
   * `-1` only while this plugin's `update` hook drives a pull, and read by
   * `appendTransaction` for the fix dispatch that pull triggers - see
   * {@link isBackwardDeletion} / {@link biasCaretLeft}. The window is
   * synchronous (`pull` emits, lib0's binding runs the whole fix convergence,
   * and the RDT dispatches back into this view, all before `pull` returns), so
   * a plugin-instance-level variable cannot interleave between editors - as
   * long as one plugin instance serves one live editor (see `liveRdts`). The
   * user's own transaction is applied *before* `update` runs, so its
   * `appendTransaction` pass sees `0` and is untouched; remote changes arrive
   * outside the window and keep ProseMirror's default right bias.
   *
   * @type {-1 | 0}
   */
  let caretBias = 0
  /**
   * RDTs of plugin views destroyed during the current synchronous tick, keyed by
   * their EditorView. A plugin-list change (`state.reconfigure`, e.g. Tiptap's /
   * BlockNote's `registerPlugin`) destroys every plugin view and immediately
   * recreates it for the *same* EditorView; the new plugin view reclaims the
   * RDTs from here instead of rebuilding the binding (whose initial sync is
   * O(document)). ProseMirror gives `destroy` no way to tell that apart from
   * `EditorView.destroy()`, so unclaimed RDTs are torn down in a microtask.
   *
   * @type {Map<import('prosemirror-view').EditorView, { rdts: SyncRdts, pluginState: SyncPluginState, doc: import('prosemirror-model').Node }>}
   */
  const handoffs = new Map()
  /**
   * The RDTs of this plugin instance's *mounted* plugin views (parked
   * `handoffs` are not live). Only used to detect an unsupported setup: one
   * plugin instance live in several EditorViews bound to the same ytype. Every
   * binding writes to Y with the plugin instance as origin and drops events
   * carrying that origin as its own echo, so those views would silently miss
   * each other's edits (the undo origin and `caretBias` are shared too).
   *
   * @type {Set<SyncRdts>}
   */
  const liveRdts = new Set()
  let warnedShared = false
  return new Plugin({
    key: ySyncPluginKey,
    state: {
      init: () => {
        return $syncPluginState.expect({
          ytype: null,
          renderer: null,
          attributionMapper: opts.mapAttributionToMark || defaultMapAttributionToMark,
          attributedNodes: opts.attributedNodes || defaultAttributedNodes,
          customCompare: opts.customCompare || null,
          isInitialContent: opts.isInitialContent || null,
          binding: null
        })
      },
      apply: (tr, prevPluginState) => {
        const stateUpdate = $maybeSyncPluginStateUpdate.expect(tr.getMeta(ySyncPluginKey) || null)
        if (!stateUpdate) {
          return prevPluginState
        }
        return object.assign({}, prevPluginState, stateUpdate, stateUpdate.renderer == null ? { renderer: null } : {})
      }
    },
    appendTransaction: (trs, oldState, newState) =>
      caretBias === -1 ? biasCaretLeft(trs, oldState, newState) : null,
    view (initialView) {
      /**
       * @type {SyncRdts | null}
       */
      let rdts = null
      /**
       * The plugin state and document `rdts` were last synced against - handed
       * over on `destroy` so a reclaiming plugin view can catch up.
       */
      let syncedPluginState = $syncPluginState.cast(ySyncPluginKey.getState(initialView.state))
      let syncedDoc = initialView.state.doc
      const teardown = () => {
        if (rdts == null) return
        liveRdts.delete(rdts)
        destroyRdts(rdts)
        rdts = null
      }
      /**
       * (Re)create both RDTs and the binding for the current plugin state.
       * A fresh binding also means fresh transformer state (the
       * `fullAttributions` overlay tracks the change stream and must never be
       * reused across a ytype/renderer switch).
       *
       * @param {import('prosemirror-view').EditorView} view
       * @param {SyncPluginState} pluginState
       */
      const setup = (view, pluginState) => {
        teardown()
        const ytype = pluginState.ytype
        if (ytype == null) {
          // paused - clear the exposed binding. `renderer` must ride along on every
          // state-update meta: `apply` force-nulls it when omitted.
          if (pluginState.binding != null) {
            view.dispatch(view.state.tr.setMeta(ySyncPluginKey, $syncPluginStateUpdate.expect({
              binding: null,
              renderer: pluginState.renderer
            })).setMeta('addToHistory', false))
          }
          return
        }
        const renderer = pluginState.renderer || null
        // Attribution exists only where a renderer produces it (`renderer: null`
        // renders plain content - see YSyncRdt, and `@y/y`'s no-renderer path,
        // which hardcodes `attrs: null`). A renderer-less binding therefore
        // never applies a `y-attributed-*` mark, so auditing the schema for
        // them would be noise. Setting a renderer is the moment the integrator
        // asks for suggestions / versioning - and a renderer change rebuilds
        // the binding, so this gate is also "audit once, when a renderer is set".
        if (renderer != null) warnUnsupportedAttributionMarks(view.state.schema)
        const compare = pluginState.customCompare
        const attributionMapper = pluginState.attributionMapper
        const yRdt = new YSyncRdt({
          ytype,
          renderer,
          origin: ySyncPluginKey.get(view.state),
          compare,
          onInternalError: opts.onInternalError ?? null
        })
        const pmRdt = new ProsemirrorRdt({
          view,
          attributedNodes: pluginState.attributedNodes,
          compare,
          onInternalError: opts.onInternalError ?? null,
          // an empty ytype must not receive the editor's schema-default
          // content — see "Initial-content gate" in ProsemirrorRdt's doc
          gateInitialContent: ytype.length === 0,
          isInitialContent: pluginState.isInitialContent,
          // capture only what the meta needs: `pluginState` also carries the
          // previous binding, which would chain every past binding together
          getMeta: () => $syncPluginStateUpdate.expect({
            change: null,
            renderer,
            attributionMapper,
            ytype
          })
        })
        // Store the rdts *before* binding: the Binding constructor runs the
        // initial sync synchronously, which dispatches into the view and
        // re-enters this plugin's `update` hook (unless `setup` runs from
        // `view()` itself - the plugin view is not registered yet then).
        rdts = { yRdt, pmRdt, binding: /** @type {any} */ (null) }
        if (!warnedShared) {
          for (const other of liveRdts) {
            if (other.yRdt.ytype === ytype && other.pmRdt.view !== view) {
              warnedShared = true
              console.warn(
                '[y/prosemirror] this syncPlugin instance is bound to the same ytype in more than one live EditorView. ' +
                "The views will not see each other's edits (they share one Y transaction origin). Create a separate " +
                'syncPlugin() (and EditorState) per editor; reusing an EditorState in a new view after destroying the ' +
                'old one is fine.'
              )
              break
            }
          }
        }
        liveRdts.add(rdts)
        const transformer = defaultTransformer({
          mapAttributionToMark: pluginState.attributionMapper,
          transformers: opts.transformers
        })
        // `diffCompare` applies `customCompare` to the initial-state sync
        // diff as well. The RDTs' own diffs (view-side pulls, fixes, the Y
        // side's uncertain-window emissions) already use it; the Y side's
        // steady-state emissions are the native change deltas — the change as
        // it actually happened — which are never re-paired by `diff`, so
        // `customCompare` does not apply there (see YSyncRdt).
        rdts.binding = bind(yRdt, pmRdt, transformer({
          getState: () => yRdt.delta,
          schema: view.state.schema
        }), { diffCompare: compare ?? undefined })
        // Expose the live binding on the plugin state so the cursor plugin can map
        // positions through its transformer. `renderer` must ride along: `apply`
        // force-nulls it when omitted, which would tear down an active renderer (and
        // re-trigger setup, forever). The re-entrant dispatch is safe - `binding` is
        // not part of the identity comparison in `update` below.
        view.dispatch(view.state.tr.setMeta(ySyncPluginKey, $syncPluginStateUpdate.expect({
          binding: rdts.binding,
          renderer: pluginState.renderer
        })).setMeta('addToHistory', false))
      }
      const handoff = handoffs.get(initialView)
      handoffs.delete(initialView)
      if (handoff != null && !handoff.rdts.pmRdt.missedDelta && !syncConfigChanged(handoff.pluginState, syncedPluginState)) {
        // Same EditorView, plugin views recreated by a plugin-list change: keep
        // the live binding. `update` is not called for a state swapped in
        // together with the new plugin list, so pull a doc change here.
        rdts = handoff.rdts
        rdts.pmRdt.suspended = false
        liveRdts.add(rdts)
        if (handoff.doc !== initialView.state.doc) rdts.pmRdt.pull(handoff.doc)
      } else {
        if (handoff != null) destroyRdts(handoff.rdts)
        // A new EditorView reusing a configured EditorState: recreate the
        // view-owned binding immediately - its initial sync also catches up Y
        // changes made while unmounted. For a fresh state (no ytype yet) this
        // returns without dispatching.
        setup(initialView, syncedPluginState)
      }
      syncedDoc = initialView.state.doc
      return {
        update (view, prevState) {
          const pluginState = $syncPluginState.cast(ySyncPluginKey.getState(view.state))
          const prevPluginState = ySyncPluginKey.getState(prevState)
          syncedPluginState = pluginState
          if (prevPluginState == null || syncConfigChanged(prevPluginState, pluginState)) {
            setup(view, pluginState)
          }
          syncedDoc = view.state.doc
          if (rdts == null) return
          // our own dispatch re-entering the hook — `applyDelta` handles state
          if (rdts.pmRdt.isApplying) return
          if (view.state.doc === prevState.doc) return
          caretBias = isBackwardDeletion(prevState.selection, view.state.selection) ? -1 : 0
          try {
            rdts.pmRdt.pull(prevState.doc)
          } finally {
            caretBias = 0
            syncedDoc = view.state.doc
          }
        },
        destroy () {
          if (rdts == null) return
          const h = { rdts, pluginState: syncedPluginState, doc: syncedDoc }
          rdts = null
          liveRdts.delete(h.rdts)
          h.rdts.pmRdt.suspended = true
          handoffs.set(initialView, h)
          queueMicrotask(() => {
            if (handoffs.get(initialView) !== h) return
            handoffs.delete(initialView)
            destroyRdts(h.rdts)
          })
        }
      }
    }
  })
}
